/**
 * Platform admin card: seed or refresh YAML framework definitions into
 * Postgres (POST /api/admin/frameworks/seed).
 */
import React, { useState } from 'react';
import { ActivityIndicator, Switch, TextInput, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from '../../hooks/useTranslation';
import { confirmDialog, showDialog } from '../../utils/dialogs';
import { parseFrameworkSeedKeys } from '../../screens/admin/platform/platformAdminFormat';

export default function FrameworkSeedingCard() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [frameworkSeedKeys, setFrameworkSeedKeys] = useState<string>('icf_2024');
  const [frameworkSeedOverwrite, setFrameworkSeedOverwrite] = useState(false);
  const [frameworkSeedLoading, setFrameworkSeedLoading] = useState(false);
  const [frameworkSeedResult, setFrameworkSeedResult] = useState<{ frameworks: string[]; message: string } | null>(null);

  const handleSeedFrameworks = async () => {
    const keys = parseFrameworkSeedKeys(frameworkSeedKeys);
    if (frameworkSeedOverwrite) {
      const confirmed = await confirmDialog(
        t('platformAdmin.seedOverwriteConfirmTitle'),
        t('platformAdmin.seedOverwriteConfirm', {
          frameworks: keys.length > 0 ? keys.join(', ') : t('platformAdmin.seedOverwriteAllFrameworks'),
        }),
        t('platformAdmin.seedOverwriteAction'),
        t('common.cancel'),
      );
      if (!confirmed) return;
    }
    try {
      setFrameworkSeedLoading(true);
      setFrameworkSeedResult(null);
      const data = await apiRequest<any>({
        endpoint: '/admin/frameworks/seed',
        method: 'POST',
        data: {
          framework_keys: keys.length > 0 ? keys : null,
          overwrite: frameworkSeedOverwrite,
        },
      });
      setFrameworkSeedResult({
        frameworks: data.frameworks || [],
        message: data.message || 'Frameworks seeded',
      });
      const successMessage = data?.message || 'Frameworks seeded successfully.';
      showToast(successMessage, 'success');
    } catch (error: any) {
      const message = error?.message || 'Failed to seed frameworks.';
      showDialog(message);
    } finally {
      setFrameworkSeedLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-0">
        <Text className="text-lg font-semibold text-foreground">
          Framework Seeding
        </Text>
        <Text className="text-sm text-muted-foreground mt-1">
          Seed or refresh YAML framework definitions into Postgres. Use comma-separated keys or leave blank for all.
        </Text>
      </CardHeader>
      <CardContent className="gap-4">
        <View className="gap-2">
          <Text className="text-sm font-semibold text-foreground">
            Framework Keys
          </Text>
          <TextInput
            value={frameworkSeedKeys}
            onChangeText={setFrameworkSeedKeys}
            placeholder="icf_2024"
            autoCapitalize="none"
            autoCorrect={false}
            className="border border-slate-200 rounded-xl px-4 py-3 text-sm text-foreground bg-white"
          />
          <Text className="text-xs text-muted-foreground">
            Example: icf_2024, my_school_framework
          </Text>
        </View>
        <View className="flex-row items-center justify-between">
          <View className="flex-1 pr-3">
            <Text className="text-sm font-semibold text-foreground">Overwrite Existing</Text>
            <Text className="text-xs text-muted-foreground mt-1">
              If enabled, existing markers and levels will be replaced.
            </Text>
          </View>
          <Switch
            value={frameworkSeedOverwrite}
            onValueChange={setFrameworkSeedOverwrite}
            thumbColor={frameworkSeedOverwrite ? ADMIN_COLORS.primary : '#f4f4f5'}
            trackColor={{ true: '#bfdbfe', false: '#e5e7eb' }}
          />
        </View>
        {frameworkSeedResult ? (
          <View className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
            <Text className="text-xs font-semibold text-emerald-700">
              {frameworkSeedResult.message}
            </Text>
            {frameworkSeedResult.frameworks?.length > 0 ? (
              <Text className="text-xs text-emerald-700 mt-1">
                Seeded: {frameworkSeedResult.frameworks.join(', ')}
              </Text>
            ) : null}
          </View>
        ) : null}
        <Button
          variant="default"
          disabled={frameworkSeedLoading}
          onPress={handleSeedFrameworks}
          className="flex-row items-center justify-center gap-2"
        >
          {frameworkSeedLoading ? (
            <>
              <ActivityIndicator size="small" color="#ffffff" />
              <Text className="text-sm font-semibold text-primary-foreground">
                Seeding...
              </Text>
            </>
          ) : (
            <>
              <MaterialIcons name="upload" size={18} color="#ffffff" />
              <Text className="text-sm font-semibold text-primary-foreground">
                Seed Frameworks
              </Text>
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
